import { SEGMENTOS_EXAMEN } from './segmentosExamen';
import {
  esV2, fraseLesionV2, fragmentosLesionV2, nivelLesionV2, segmentoDeUbicacion,
  valoracionesDe, primeraValoracion, fraseControlV2, cabezaCorta, lesionAlCrear, lesionEn,
  MOTIVO_RECLASIFICACION, CATEGORIA_LEGADO, CLASIFICACION, PENDIENTE, conCuracion, concuerda, serieDe,
} from './lesionPiel';

// Piel y puntos de apoyo — dominio puro del mapa corporal (piloto B2B).
//
// El dolor institucional que ataca este módulo son las LESIONES POR PRESIÓN:
// la foto de piel al recibir (con hora sellada) y el contraste con lo que
// aparece durante el turno son EL dato que Calidad audita — una LPP registrada
// al ingreso "venía de antes"; una pesquisada intra-turno con su hora es
// vigilancia activa, no una falta (anti-culpa hasta en el dato).
//
// Todo es catálogo cerrado (zona + tipo + categoría + cobertura), sin campo de
// nota libre a propósito: el contexto se dicta a la cronología, como siempre.
// Catálogo cerrado no significa dato anónimo: una lesión con su zona y su hora
// es un dato clínico de una persona identificable por su cama y su turno. Por
// eso no viaja a la telemetría ni sale del registro efímero del turno; dentro
// de él alimenta el examen, la evolución y el SBAR como cualquier otro dato.
//
// Desde 2026-10-10 conviven dos formas de lesión: la histórica (zona de 12
// puntos, una línea) y la v2 de lesionPiel.js (ubicación libre, valoraciones
// sucesivas, historia). `esV2` las distingue y cada lector delega; la histórica
// se lee y se imprime exactamente igual que antes.
//
// Persistencia: `registro.examenFisico.piel = { lesiones: [], revisiones: [] }`
// — clave opcional dentro del jsonb `examen_fisico` que ya viaja al remoto
// (drafts viejos no la traen y todos los lectores son tolerantes).

// ── Catálogos ─────────────────────────────────────────────────────────────────

// Zonas de presión canónicas (superset de la LPP legada del examen —
// CAMPOS_EXAMEN.sacra.lpp_localizacion — y del catálogo tegumentario de la
// evolución). `vista` ubica la zona en la silueta del mapa; `lados` habilita
// derecha/izquierda; `pos` son coordenadas del viewBox (x para una instancia,
// la otra se espeja — la lateralidad la resuelve el mapa según la vista).
export const ZONAS_PRESION = [
  { id: 'occipucio', label: 'Occipucio', vista: 'posterior', lados: null, pos: { x: 50, y: 7 } },
  // Nariz y boca/labios: lesiones por DISPOSITIVO (SNG/naricera en nariz,
  // TET/comisura en boca) — las MDRPI que una UCI audita tanto como el sacro.
  { id: 'nariz', label: 'Nariz', vista: 'anterior', lados: null, pos: { x: 50, y: 6.5 } },
  { id: 'boca', label: 'Boca y labios', vista: 'anterior', lados: null, pos: { x: 50, y: 11 } },
  { id: 'oreja', label: 'Oreja', vista: 'anterior', lados: ['der', 'izq'], pos: { x: 41, y: 8 } },
  { id: 'escapula', label: 'Escápula', vista: 'posterior', lados: ['der', 'izq'], pos: { x: 38, y: 24 } },
  { id: 'codo', label: 'Codo', vista: 'posterior', lados: ['der', 'izq'], pos: { x: 23, y: 38 } },
  { id: 'sacro', label: 'Sacro', vista: 'posterior', lados: null, pos: { x: 50, y: 44 } },
  { id: 'isquion', label: 'Isquion', vista: 'posterior', lados: ['der', 'izq'], pos: { x: 43, y: 52 } },
  { id: 'trocanter', label: 'Trocánter', vista: 'posterior', lados: ['der', 'izq'], pos: { x: 34, y: 48 } },
  { id: 'rodilla', label: 'Rodilla', vista: 'anterior', lados: ['der', 'izq'], pos: { x: 42.75, y: 66 } },
  { id: 'maleolo', label: 'Maléolo', vista: 'anterior', lados: ['der', 'izq'], pos: { x: 42.75, y: 90 } },
  { id: 'talon', label: 'Talón', vista: 'posterior', lados: ['der', 'izq'], pos: { x: 43, y: 94 } },
  // "Otra zona" no vive en la silueta: se registra desde el listado del panel.
  { id: 'otra', label: 'Otra zona', vista: null, lados: null, pos: null },
];

// Tipos de lesión de piel. DTPI no es un tipo aparte: es un estadio de LPP
// (va en `categoria`), igual que en la escala LPP de la evolución.
export const TIPOS_LESION = [
  'LPP', 'Eritema que no blanquea', 'DAI', 'Herida', 'Maceración', 'Otra',
];

// Estadios de LPP — MISMOS strings que CAMPOS_EXAMEN.sacra.lpp_categoria
// (continuidad del dato de Calidad; el puente queda fijado por test).
export const CATEGORIAS_LPP = [
  'Categoría I', 'Categoría II', 'Categoría III', 'Categoría IV', 'DTPI', 'No estadiable',
];

// Coberturas — MISMOS strings que lppCobertura del tegumentario de la evolución.
export const COBERTURAS_LESION = [
  'Hidrocoloide', 'Espuma hidrocelular', 'Alginato', 'Apósito de plata', 'Gasa vaselinada', 'Sin cobertura',
];

export const LADO_LABEL = { der: 'derecho', izq: 'izquierdo' };

// Medidas de prevención de LPP declarables (iteración pre-Play, pieza 6):
// catálogo cerrado a nivel de PACIENTE (el colchón es del paciente, no de una
// lesión). Cierra el círculo con Braden y el riesgo: prevención constatada,
// no supuesta.
//
// Desde el módulo LPP (2026-08-18) el genérico "Colchón antiescaras" se
// reemplazó por las categorías reales de superficie de la guía internacional
// 2025 (cap. Superficies de apoyo: SS3 espuma viscoelástica, SS5 a SS7 aire
// estático o alternante) — pedido del fundador: "en vez de decir colchón
// antiescaras, que se base en esto". Un valor viejo guardado con el string
// legado sigue imprimiéndose tal cual (lineasPiel imprime lo guardado) y la
// UI lo muestra como chip legado (ver PielPanel); no se migra.
export const PREVENCION_LPP = [
  'Colchón viscoelástico', 'Colchón de aire', 'Protección de prominencias',
  'Ácidos grasos hiperoxigenados', 'Taloneras', 'Cojín de posicionamiento',
  'Protección bajo dispositivo',
];

// Valores del catálogo ANTERIOR que se siguen LEYENDO (jamás se ofrecen ni se
// migran): un registro del mismo día con "Colchón antiescaras" marcado sigue
// imprimiendo su medida en vez de perderla al filtrar. Es legado corto (los
// registros de turno son efímeros), pero la regla es la de siempre: datos
// viejos se leen con adapters, la clave guardada no desaparece bajo los dedos.
export const PREVENCION_LPP_LEGADO = ['Colchón antiescaras'];
const PREVENCION_LEIBLE = [...PREVENCION_LPP, ...PREVENCION_LPP_LEGADO];

// ── Describir la herida (pedagógico, opcional) ────────────────────────────────
// El fundador pidió que la lesión sea DESCRIBIBLE ademas de catalogada: forma
// `{k, label, tipo, opciones, notas}` CALCADA de CAMPOS_EXAMEN
// (examenFisico.js) para reusar `interpretarCampo` (escalasEvolucion.js) tal
// cual — mismo motor didáctico que el examen y la evolución, no uno nuevo.
// Sigue la misma doctrina del módulo: catálogo cerrado, SIN campo de nota
// libre (no entra texto escrito por nadie; el dato sigue siendo clínico).
export const DESCRIPTORES_HERIDA = [
  { k: 'lecho', label: 'Lecho de la herida', tipo: 'multi',
    opciones: ['Granulatorio', 'Esfacelado', 'Necrótico', 'Epitelizado'],
    notas: {
      Granulatorio: { nivel: 'normal', texto: 'Tejido rojo y firme: la herida está regenerando. Es lo que quieres ver.' },
      Esfacelado:   { nivel: 'moderado', texto: 'Tejido amarillento adherido: frena la cicatrización, se maneja según el protocolo de curación.' },
      Necrótico:    { nivel: 'severo', texto: 'Tejido negro, muerto: avisar y manejar según protocolo.' },
      Epitelizado:  { nivel: 'normal', texto: 'Piel nueva rosada cubriendo la herida: etapa final de la cicatrización.' },
    } },
  { k: 'exudadoCantidad', label: 'Exudado', tipo: 'select',
    opciones: ['Sin exudado', 'Escaso', 'Moderado', 'Abundante'],
    notas: {
      'Sin exudado': { nivel: 'normal', texto: 'Herida seca: correlaciona con el apósito elegido.' },
      Escaso:        { nivel: 'normal', texto: 'Lo esperado en una herida que avanza bien.' },
      Moderado:      { nivel: 'leve', texto: 'Elige el apósito según cuánto absorbe: correlaciona con la cobertura.' },
      Abundante:     { nivel: 'moderado', texto: 'Mucho exudado macera la piel de alrededor: vigilar bordes y frecuencia de curación.' },
    } },
  { k: 'exudadoTipo', label: 'Tipo de exudado', tipo: 'select',
    opciones: ['Seroso', 'Serohemático', 'Hemático', 'Purulento'],
    notas: {
      Seroso:       { nivel: 'normal', texto: 'Líquido claro: parte normal de la cicatrización.' },
      Serohemático: { nivel: 'leve', texto: 'Claro con sangre: frecuente tras curación o roce.' },
      Hemático:     { nivel: 'moderado', texto: 'Sangre franca: revisa si hubo trauma en la zona y avisa si persiste.' },
      Purulento:    { nivel: 'severo', texto: 'Pus sugiere infección local: avisar según el protocolo del servicio.' },
    } },
  { k: 'bordes', label: 'Bordes', tipo: 'select',
    opciones: ['Definidos', 'Irregulares', 'Macerados', 'Socavados'],
    notas: {
      Definidos:   { nivel: 'normal', texto: 'Borde nítido y adherido: lo esperado.' },
      Irregulares: { nivel: 'leve', texto: 'Borde disparejo: describe la forma al medir.' },
      Macerados:   { nivel: 'moderado', texto: 'Borde blanquecino por humedad: correlaciona con la cantidad de exudado.' },
      Socavados:   { nivel: 'moderado', texto: 'El borde despegado esconde más daño del que se ve: al medir, incluye lo socavado.' },
    } },
  { k: 'perilesional', label: 'Piel perilesional', tipo: 'select',
    opciones: ['Sana', 'Eritematosa', 'Macerada', 'Indurada'],
    notas: {
      Sana:        { nivel: 'normal', texto: 'Piel de alrededor indemne: lo esperado.' },
      Eritematosa: { nivel: 'leve', texto: 'Enrojecida: vigila que no se extienda, correlaciona con infección local.' },
      Macerada:    { nivel: 'moderado', texto: 'Blanca y húmeda: proteger del exudado, correlaciona con la cobertura.' },
      Indurada:    { nivel: 'moderado', texto: 'Dura al tacto: correlaciona con inflamación profunda, avisar si es nueva.' },
    } },
  // Sin notas por opción: son banderas que se leen EN CONJUNTO (la pista de
  // seguridad del panel las agrupa), no una didáctica individual por síntoma.
  { k: 'infeccion', label: 'Signos de infección local', tipo: 'multi',
    opciones: ['Eritema que se extiende', 'Calor local', 'Aumento del dolor', 'Exudado purulento', 'Mal olor', 'Edema perilesional'] },
];

// Severidad de la DAI (escala Ghent) — MISMOS strings que ESCALAS_DEF.dai
// (DAI_OPTS) de escalasEvolucion.js: el puente queda fijado por test. No se
// importa escalasEvolucion acá para no crear el ciclo (escalasEvolucion YA
// importa pielLpp para el catálogo de coberturas/zonas del tegumentario).
export const DAI_SEVERIDAD = ['Categoría 1A', 'Categoría 1B', 'Categoría 2A', 'Categoría 2B'];

// ── Zonas a vigilar por dispositivo (MDRPI proactivo, pieza 3) ───────────────
// El mapa piensa como enfermera de UCI: si el registro declara una SNG, la
// nariz se vigila; un TOT, la boca; una TQT, el cuello del estoma (vive en
// 'otra' hasta tener zona propia: no se inventa topografía). El soporte
// respiratorio del chequeo general suma lo suyo (CNAF y naricera marcan nariz
// y orejas; VNI el puente nasal). Todo por ids de catálogos existentes:
// SUGIERE revisar, jamás pre-marca una lesión (anti-culpa: mirar es tuyo).
const ZONAS_POR_DISPOSITIVO = {
  sng: [{ zona: 'nariz', motivo: 'SNG' }],
  // La nasoyeyunal entra por la nariz igual que la SNG, así que vigila la misma
  // zona por la misma razón (MDRPI del ala nasal). Se agregó con el dispositivo
  // el 2026-09-21: sumar un invasivo nasal sin su zona lo habría dejado fuera
  // de la vigilancia proactiva sin que nadie lo notara.
  sny: [{ zona: 'nariz', motivo: 'sonda nasoyeyunal' }],
  tet: [{ zona: 'boca', motivo: 'TOT' }],
};
const ZONAS_POR_SOPORTE = {
  'CNAF': [{ zona: 'nariz', motivo: 'CNAF' }, { zona: 'oreja', motivo: 'CNAF' }],
  'VNI': [{ zona: 'nariz', motivo: 'VNI' }],
  'Oxigenoterapia': [{ zona: 'nariz', motivo: 'O2' }, { zona: 'oreja', motivo: 'O2' }],
};

// → [{ zona (id de ZONAS_PRESION), label, motivo }] dedup por zona (el primer
// motivo gana). Pura y tolerante: sin dispositivos ni soporte → [].
export function zonasVigiladas({ dispositivos = [], soporteRespiratorio = null, o2Interfaz = null } = {}) {
  const out = new Map();
  const agrega = (v) => {
    if (!out.has(v.zona)) {
      const def = ZONAS_PRESION.find((z) => z.id === v.zona);
      if (def) out.set(v.zona, { zona: v.zona, label: def.label, motivo: v.motivo });
    }
  };
  for (const d of dispositivos || []) {
    for (const v of ZONAS_POR_DISPOSITIVO[d?.tipo] || []) agrega(v);
  }
  for (const v of ZONAS_POR_SOPORTE[soporteRespiratorio] || []) {
    // La interfaz declarada afina el motivo (naricera vs O2 genérico).
    agrega(soporteRespiratorio === 'Oxigenoterapia' && o2Interfaz === 'Naricera' ? { ...v, motivo: 'naricera' } : v);
  }
  return [...out.values()];
}

// Zona del mapa → segmento del examen céfalo-caudal: una lesión marcada en el
// mapa TAMBIÉN enciende su segmento en la grilla (unir el mapa al examen —
// marcar en uno no puede dejar al otro "vacío"). Talón vive en sacra porque
// el segmento es "zona sacra y puntos de apoyo" (mismo criterio del catálogo).
export const SEGMENTO_POR_ZONA = {
  occipucio: 'cabeza', nariz: 'cabeza', boca: 'cabeza', oreja: 'cabeza',
  escapula: 'espalda', codo: 'eess', sacro: 'sacra', isquion: 'sacra',
  trocanter: 'sacra', rodilla: 'eeii', maleolo: 'eeii', talon: 'sacra',
};

// Segmentos del examen que tienen al menos una lesión del mapa (incluye las
// legadas): alimenta la completitud de la grilla céfalo-caudal.
export function segmentosConLesion(examenFisico) {
  const out = new Set();
  for (const l of [...pielState(examenFisico).lesiones, ...lesionesLegacySacra(examenFisico)]) {
    // La v2 sabe su región aunque no sea un punto de apoyo (un desgarro en el
    // antebrazo enciende extremidades superiores).
    const seg = esV2(l) ? segmentoDeUbicacion(l.ubicacion) : SEGMENTO_POR_ZONA[l.zona];
    if (seg) out.add(seg);
  }
  return out;
}

// ── Constructores ─────────────────────────────────────────────────────────────

// Id local (mismo espíritu que nuevoId de registroTurno; copia mínima para no
// crear el ciclo registroTurno → examenFisico → pielLpp → registroTurno).
function idLesion() {
  return `pl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function horaDe(ahora) {
  const d = ahora instanceof Date ? ahora : new Date(ahora);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// Lesión estructurada. `presenteAlIngreso` es LA distinción médico-legal del
// módulo (venía de antes vs apareció en el turno); la hora se sella al crear y
// es propia de cada lesión (no depende de la hora global del examen). Los
// valores fuera de catálogo se normalizan — jamás aterriza un string libre.
export function nuevaLesion({ zona, lado = null, tipo, categoria = null, cobertura = null, medidas = null, descriptores = null, presenteAlIngreso, ahora = new Date() } = {}) {
  const zonaDef = ZONAS_PRESION.find((z) => z.id === zona) || ZONAS_PRESION.find((z) => z.id === 'otra');
  const tipoOk = TIPOS_LESION.includes(tipo) ? tipo : 'Otra';
  const base = {
    id: idLesion(),
    zona: zonaDef.id,
    lado: zonaDef.lados && zonaDef.lados.includes(lado) ? lado : null,
    tipo: tipoOk,
    // La categoría es el estadio de una LPP — sin LPP no hay estadio.
    categoria: tipoOk === 'LPP' && CATEGORIAS_LPP.includes(categoria) ? categoria : null,
    cobertura: COBERTURAS_LESION.includes(cobertura) ? cobertura : null,
    presenteAlIngreso: Boolean(presenteAlIngreso),
    hora: horaDe(ahora),
    ts: ahora instanceof Date ? ahora.getTime() : ahora,
  };
  // Medidas en cm (pieza 2 pre-Play): NÚMEROS opcionales, el estándar de la
  // curación real. Solo se guarda la clave si al menos una medida es un
  // número de verdad (retrocompatible: sin medidas, el shape es el de antes).
  const m = normalizarMedidas(medidas);
  const out = m ? { ...base, medidas: m } : base;
  // Describir la herida (opcional, pedagógico): mismo trato que las medidas,
  // la clave solo se guarda si algo sobrevivió a la normalización — una
  // lesión sin descriptores queda BYTE A BYTE igual que antes de esta pieza.
  const d = normalizarDescriptores(tipoOk, descriptores);
  return d ? { ...out, descriptores: d } : out;
}

// '3' / '3.5' / '3,5' → '3,5' (coma decimal chilena en el documento); todo lo
// demás se descarta. Jamás aterriza texto libre en la lesión.
const MEDIDA_RE = /^\d{1,2}([.,]\d)?$/;
function normalizarMedidas(medidas) {
  if (!medidas || typeof medidas !== 'object') return null;
  const limpia = (v) => {
    const t = String(v ?? '').trim().replace('.', ',');
    return MEDIDA_RE.test(t.replace(',', '.')) || MEDIDA_RE.test(t) ? t : null;
  };
  const largo = limpia(medidas.largo);
  const ancho = limpia(medidas.ancho);
  if (!largo && !ancho) return null;
  return { largo, ancho };
}

// Espejo de normalizarMedidas para "describir la herida": cada valor se
// filtra contra SU catálogo (select fuera de catálogo se descarta, multi se
// filtra elemento a elemento) — un string libre jamás aterriza. `daiSeveridad`
// solo tiene sentido con tipo DAI (mismo criterio que categoria con LPP). Si
// nada sobrevive, devuelve null y la clave `descriptores` no se guarda.
function normalizarDescriptores(tipo, d) {
  if (!d || typeof d !== 'object') return null;
  const out = {};
  for (const def of DESCRIPTORES_HERIDA) {
    const v = d[def.k];
    if (def.tipo === 'multi') {
      const limpio = Array.isArray(v) ? v.filter((x) => def.opciones.includes(x)) : [];
      if (limpio.length) out[def.k] = limpio;
    } else if (def.opciones.includes(v)) {
      out[def.k] = v;
    }
  }
  if (tipo === 'DAI' && DAI_SEVERIDAD.includes(d.daiSeveridad)) out.daiSeveridad = d.daiSeveridad;
  return Object.keys(out).length ? out : null;
}

// Revisión de piel: el hecho de HABER MIRADO también es dato (y respaldo).
// 'ingreso'/'indemne' = la foto normal en 1 toque; 'control'/'sin_cambios' =
// la vigilancia intra-turno que la entrega puede constatar con hora.
export function nuevaRevision({ contexto, resultado, ahora = new Date() } = {}) {
  return {
    id: idLesion(),
    contexto: contexto === 'control' ? 'control' : 'ingreso',
    resultado: resultado === 'sin_cambios' ? 'sin_cambios' : 'indemne',
    hora: horaDe(ahora),
    ts: ahora instanceof Date ? ahora.getTime() : ahora,
  };
}

// ── Lectores tolerantes ───────────────────────────────────────────────────────

export function pielState(examenFisico) {
  const p = examenFisico?.piel;
  return {
    lesiones: Array.isArray(p?.lesiones) ? p.lesiones : [],
    revisiones: Array.isArray(p?.revisiones) ? p.revisiones : [],
    // Prevención declarada (pieza 6): clave opcional, catálogo cerrado
    // (vigente + legado leído; lo demás jamás imprime).
    prevencion: Array.isArray(p?.prevencion) ? p.prevencion.filter((x) => PREVENCION_LEIBLE.includes(x)) : [],
  };
}

export function pielTieneContenido(examenFisico) {
  const { lesiones, revisiones, prevencion } = pielState(examenFisico);
  return lesiones.length > 0 || revisiones.length > 0 || prevencion.length > 0;
}

// Declara el set completo de medidas de prevención (pura; fuera de catálogo
// se filtra al leer, así un dato viejo raro jamás imprime).
export function conPrevencion(examenFisico, lista) {
  const ex = examenFisico || {};
  const { lesiones, revisiones } = pielState(ex);
  const limpia = (Array.isArray(lista) ? lista : []).filter((x) => PREVENCION_LEIBLE.includes(x));
  return { ...ex, piel: { lesiones, revisiones, prevencion: limpia } };
}

// Escrituras puras (la UI hace setExamen(conLesion(ex, lesion))).
export function conLesion(examenFisico, lesion) {
  const ex = examenFisico || {};
  const { lesiones, revisiones, prevencion } = pielState(ex);
  return { ...ex, piel: { lesiones: [...lesiones, lesion], revisiones, prevencion } };
}
// Reemplaza una lesión por su versión actualizada (misma id). La v2 guarda su
// historia DENTRO de la lesión (valoraciones, curaciones, correcciones), así
// que actualizar es cambiarla entera; una id que no está no agrega nada.
export function conLesionActualizada(examenFisico, lesion) {
  const ex = examenFisico || {};
  const { lesiones, revisiones, prevencion } = pielState(ex);
  if (!lesion?.id || !lesiones.some((l) => l.id === lesion.id)) return ex;
  return { ...ex, piel: { lesiones: lesiones.map((l) => (l.id === lesion.id ? lesion : l)), revisiones, prevencion } };
}
export function sinLesion(examenFisico, lesionId) {
  const ex = examenFisico || {};
  const { lesiones, revisiones, prevencion } = pielState(ex);
  return { ...ex, piel: { lesiones: lesiones.filter((l) => l.id !== lesionId), revisiones, prevencion } };
}
export function conRevision(examenFisico, revision) {
  const ex = examenFisico || {};
  const { lesiones, revisiones, prevencion } = pielState(ex);
  return { ...ex, piel: { lesiones, revisiones: [...revisiones, revision], prevencion } };
}

export function ultimaRevisionPiel(examenFisico, contexto = null) {
  const { revisiones } = pielState(examenFisico);
  const filtradas = contexto ? revisiones.filter((r) => r.contexto === contexto) : revisiones;
  if (!filtradas.length) return null;
  return filtradas.reduce((max, r) => ((r.ts || 0) > (max.ts || 0) ? r : max));
}

// ¿La piel quedó constatada INDEMNE al recibir? Lector puro, una sola fuente
// para las dos superficies que lo preguntan (el módulo "Estado de la piel" del
// examen y el propio PielPanel) — antes cada una lo derivaba a mano de
// `revisiones` y podían desalinearse.
export function pielIndemneAlIngreso(examenFisico) {
  return !hayAlteracionesCutaneas(examenFisico) && pielState(examenFisico).revisiones
    .some((r) => r.contexto === 'ingreso' && r.resultado === 'indemne');
}

// ── Severidad y estado por zona (motor de color del mapa) ────────────────────

// 'sin_clasificar' va bajo todo: no afirma gravedad, solo que hay algo.
const ORDEN_NIVEL = { sin_clasificar: 0, leve: 1, moderado: 2, severo: 3 };

// Mismos niveles que las notas clínicas de CAMPOS_EXAMEN.sacra y del
// tegumentario de la evolución (leve/moderado/severo → tokens clinical-*).
// Una LPP sin categoría es 'sin_clasificar' (2026-10-10): antes se pintaba
// moderada, y eso afirmaba una gravedad que nadie registró. DTPI y no
// estadiable son daño profundo o de extensión que no se ve: severas, igual
// que en la v2 (el color no reinterpreta el dato, solo lo pinta).
export function nivelLesion(lesion) {
  if (!lesion) return 'leve';
  if (esV2(lesion)) return nivelLesionV2(lesion);
  if (lesion.tipo === 'LPP') {
    const c = CATEGORIA_LEGADO[lesion.categoria] || lesion.categoria;
    if (c === 'Categoría III' || c === 'Categoría IV' || c === 'No clasificable' || c === 'Tejidos profundos') return 'severo';
    if (c === 'Categoría II') return 'moderado';
    if (c === 'Categoría I') return 'leve';
    return 'sin_clasificar';
  }
  if (lesion.tipo === 'Eritema que no blanquea' || lesion.tipo === 'Maceración' || lesion.tipo === 'Otra') return 'leve';
  return 'moderado'; // DAI, Herida
}

// Estado de una zona para pintarla: null (sin datos) | 'indemne' | nivel
// máximo de sus lesiones (incluye las legadas de sacra). Con `lado`, mira solo
// ese lado (una lesión sin lado en zona bilateral cuenta para ambos).
export function estadoZona(examenFisico, zonaId, lado) {
  const lesiones = [...pielState(examenFisico).lesiones, ...lesionesLegacySacra(examenFisico)]
    .filter((l) => l.zona === zonaId)
    .filter((l) => lado === undefined || l.lado === null || l.lado === lado);
  if (lesiones.length) {
    return lesiones.map(nivelLesion).sort((a, b) => ORDEN_NIVEL[b] - ORDEN_NIVEL[a])[0];
  }
  const indemne = pielState(examenFisico).revisiones.some((r) => r.resultado === 'indemne');
  return indemne ? 'indemne' : null;
}

// ── Legado: la LPP estructurada de sacra (pre-mapa) se LEE, no se migra ──────
// Un examen viejo con lpp_categoria/lpp_localizacion pinta su lesión en el
// mapa como registro anterior (solo lectura). Presente al ingreso por
// definición: esos campos vivían en el examen de ingreso.
const ZONA_LEGACY = {
  Sacro: 'sacro', 'Talón': 'talon', Isquion: 'isquion', 'Trocánter': 'trocanter',
  Occipucio: 'occipucio', 'Otra zona de apoyo': 'otra',
};

export function lesionesLegacySacra(examenFisico) {
  const campos = examenFisico?.segmentos?.sacra?.campos;
  if (!campos || typeof campos !== 'object') return [];
  const categoria = CATEGORIAS_LPP.includes(campos.lpp_categoria) ? campos.lpp_categoria : null;
  const zona = ZONA_LEGACY[campos.lpp_localizacion] || null;
  if (!categoria && !zona) return [];
  return [{
    id: 'legacy-sacra',
    zona: zona || 'sacro',
    lado: null,
    tipo: 'LPP',
    categoria,
    cobertura: null,
    presenteAlIngreso: true,
    hora: examenFisico?.hora || null,
    ts: examenFisico?.ts || null,
    legacy: true,
  }];
}

// ── Texto (examen, PAE, evolución) ───────────────────────────────────────────

function zonaTexto(lesion) {
  const def = ZONAS_PRESION.find((z) => z.id === lesion.zona);
  const base = (def?.label || 'otra zona').toLowerCase();
  return lesion.lado ? `${base} ${LADO_LABEL[lesion.lado]}` : base;
}

function tipoTexto(lesion) {
  if (lesion.tipo !== 'LPP') {
    if (lesion.tipo === 'Otra') return 'lesión de piel';
    if (lesion.tipo === 'DAI') return 'DAI'; // sigla: conserva mayúscula
    return lesion.tipo.toLowerCase();
  }
  if (!lesion.categoria) return 'LPP';
  if (lesion.categoria === 'DTPI') return 'DTPI (lesión de tejido profundo)';
  if (lesion.categoria === 'No estadiable') return 'LPP no estadiable';
  // "Categoría II" → "categoría II": baja la inicial, conserva el numeral romano.
  return `LPP ${lesion.categoria.replace('Categoría', 'categoría')}`;
}

// "Describir la herida" en prosa: RE-VALIDA cada valor contra su catálogo al
// leer (doctrina MEDIDA_RE: un jsonb adulterado o inyectado a mano jamás
// imprime texto fuera de catálogo). Devuelve los 6 fragmentos EN ORDEN
// (daiSeveridad, lecho, exudado, bordes, perilesional, infección); cada uno
// null si no aplica o no sobrevive la revalidación.
function fragmentosDescriptores(lesion) {
  const d = lesion.descriptores;
  if (!d || typeof d !== 'object') return [null, null, null, null, null, null];
  const defDe = (k) => DESCRIPTORES_HERIDA.find((x) => x.k === k);
  const multiOk = (v, opciones) => (Array.isArray(v) ? v.filter((x) => opciones.includes(x)) : []);
  const selectOk = (v, opciones) => (opciones.includes(v) ? v : null);

  // Severidad DAI: solo con tipo DAI (espejo de categoria/LPP). "Categoría 2A"
  // baja solo la C inicial, conserva el numeral: "categoría 2A".
  const daiFrag = lesion.tipo === 'DAI' && DAI_SEVERIDAD.includes(d.daiSeveridad)
    ? d.daiSeveridad.replace('Categoría', 'categoría')
    : null;

  const lecho = multiOk(d.lecho, defDe('lecho').opciones);
  const lechoFrag = lecho.length ? `lecho ${lecho.map((x) => x.toLowerCase()).join(' y ')}` : null;

  // Exudado: "sin exudado" OMITE el tipo aunque esté guardado (no hay tipo
  // que describir de lo que no existe); con cantidad y tipo, ambos en prosa.
  const exCant = selectOk(d.exudadoCantidad, defDe('exudadoCantidad').opciones);
  const exTipo = selectOk(d.exudadoTipo, defDe('exudadoTipo').opciones);
  let exudadoFrag = null;
  if (exCant === 'Sin exudado') exudadoFrag = 'sin exudado';
  else if (exCant && exTipo) exudadoFrag = `exudado ${exCant.toLowerCase()} ${exTipo.toLowerCase()}`;
  else if (exCant) exudadoFrag = `exudado ${exCant.toLowerCase()}`;
  else if (exTipo) exudadoFrag = `exudado ${exTipo.toLowerCase()}`;

  const bordesOk = selectOk(d.bordes, defDe('bordes').opciones);
  const bordesFrag = bordesOk ? `bordes ${bordesOk.toLowerCase()}` : null;

  const periOk = selectOk(d.perilesional, defDe('perilesional').opciones);
  const periFrag = periOk ? `piel perilesional ${periOk.toLowerCase()}` : null;

  const infeccion = multiOk(d.infeccion, defDe('infeccion').opciones);
  const infeccionFrag = infeccion.length
    ? `signos de infección local: ${infeccion.map((x) => x.toLowerCase()).join(', ')}`
    : null;

  return [daiFrag, lechoFrag, exudadoFrag, bordesFrag, periFrag, infeccionFrag];
}

// ANCLA de la lesión: la cabeza de su frase ("LPP categoría II en sacro"), que
// es el fragmento ESTABLE — no cambia con la cobertura, las medidas ni la hora
// de la última curación, que sí se editan durante el turno.
//
// Existe para el puente piel → campos del tegumentario
// (turnoEvolucionBridge.js): ese puente omite del documento la línea de campos
// porque la frase completa de la lesión ya está en la nota del bloque, y antes
// de omitir tiene que CONSTATAR que esa frase sigue ahí (la nota es un campo
// libre que la enfermera reescribe, y la guarda de PII dura puede descartarla
// entera). Sale de la misma composición que `fraseLesion` para que no puedan
// divergir: si la frase se arma distinto, el ancla también.
export function anclaLesion(lesion) {
  if (esV2(lesion)) return fragmentosLesionV2(lesion).ancla;
  return `${tipoTexto(lesion)} en ${zonaTexto(lesion)}`;
}

// La frase que `mergePielEnEvolucion` escribe cuando la piel se constató
// indemne al recibir. Exportada por lo mismo que `anclaLesion`: es el ancla del
// caso sin lesiones, y un literal repetido en dos módulos se desincroniza.
export function hayAlteracionesCutaneas(ex) {
  return SEGMENTOS_EXAMEN.some((seg) => seg.hallazgos.some((h) => h.evolucion === 'tegumentario' && ex?.segmentos?.[seg.id]?.hallazgos?.includes(h.v)));
}
export function frasePielAlRecibir(ex) {
  return hayAlteracionesCutaneas(ex) ? 'Sin lesiones por presión al recibir.' : FRASE_PIEL_INDEMNE;
}
export const FRASE_PIEL_INDEMNE = 'Piel indemne al recibir, sin lesiones por presión.';

// Medidas de la lesión como texto ("3 x 2 cm" o "3 cm"), o null. Se RE-VALIDAN
// al leer: el jsonb viaja al remoto y vuelve por merge, y este módulo solo
// imprime catálogo, así que un valor raro guardado por otra vía jamás
// imprime. Vive suelta porque la consumen DOS superficies (la frase de la
// lesión y el puente de piel → campos del tegumentario, turnoEvolucionBridge):
// una sola función, cero textos paralelos.
export function medidasLesionTexto(lesion, valoracion) {
  if (esV2(lesion)) {
    const md = (valoracion || primeraValoracion(lesion))?.medidas || {};
    const num = (x) => typeof x === 'string' && /^\d{1,3}(,\d{1,2})?$/.test(x);
    if (!num(md.largo) && !num(md.ancho)) return null;
    return num(md.largo) && num(md.ancho) ? `${md.largo} x ${md.ancho} cm` : `${num(md.largo) ? md.largo : md.ancho} cm`;
  }
  const m = lesion?.medidas || {};
  const largoOk = MEDIDA_RE.test(String(m.largo ?? '').replace(',', '.')) ? m.largo : null;
  const anchoOk = MEDIDA_RE.test(String(m.ancho ?? '').replace(',', '.')) ? m.ancho : null;
  if (!largoOk && !anchoOk) return null;
  return largoOk && anchoOk ? `${largoOk} x ${anchoOk} cm` : `${largoOk || anchoOk} cm`;
}

// Los fragmentos de la frase de una lesión, CON NOMBRE. `fraseLesion` los une y
// el puente piel → campos del tegumentario (turnoEvolucionBridge.js) elige el
// que corresponde a cada campo: la frase y sus anclas salen de la MISMA
// composición, así que no pueden divergir.
//
// Por qué con nombre y no una lista (auditoría 5): el puente omite del documento
// la línea de campos cuando la nota ya cuenta la lesión, y para eso guarda un
// ANCLA que tiene que seguir presente en la nota. Anclar TODAS las claves a la
// cabeza ("LPP categoría II en sacro") las callaba aunque el valor hubiera
// cambiado, porque la cabeza es justamente lo estable: la curación dictada a las
// 14:00 cambiaba la cobertura a apósito de plata, el campo se re-sembraba bien y
// el documento seguía imprimiendo el hidrocoloide de las 08:00. Cada clave se
// ancla al fragmento que LLEVA SU VALOR, así el ancla muere cuando el valor
// cambia y la línea vuelve a narrarse. Mismo criterio que las escalas, donde el
// ancla ya era el texto del propio score.
export function fragmentosLesion(lesion, { origen: conOrigen = true, valoracion } = {}) {
  if (esV2(lesion)) {
    // Misma forma con nombre que la histórica, para que el puente de campos
    // ancle igual. Los fragmentos que la v2 no tiene sueltos quedan en null y
    // viajan dentro de `hallazgos`.
    const f = fragmentosLesionV2(lesion, { origen: conOrigen, valoracion });
    return {
      ancla: f.ancla, dai: null, medidas: f.medidas, lecho: f.lecho, exudado: f.exudado,
      bordes: null, perilesional: null, infeccion: null,
      origen: f.origen.length ? f.origen.join(', ') : null,
      cobertura: f.cobertura, curacion: f.curacion,
      cabeza: f.cabeza, reclasificacion: f.reclasificacion, hallazgos: f.hallazgos,
    };
  }
  // "Al recibir" ES el contexto: la hora del examen no se imprime (norma
  // del fundador, 2026-08-02; sigue sellada en lesion.hora/ts para auditar).
  // La pesquisa intra-turno SÍ dice su hora: es el hallazgo nuevo del turno,
  // el contraste que la entrega y Calidad necesitan ubicar en el tiempo.
  // `origen: false` (2026-09-08): bajo un rótulo que YA dice la procedencia
  // ("Al recibir: …" en la evolución) la lesión no la repite; el dato
  // `presenteAlIngreso` no cambia, solo deja de decirse dos veces en la
  // misma frase (caso N de la evaluación).
  const origen = !conOrigen ? null : lesion.presenteAlIngreso
    ? 'presente al recibir'
    : `pesquisada en el turno${lesion.hora ? ` a las ${lesion.hora}` : ''}`;
  const cobertura = lesion.cobertura
    ? lesion.cobertura === 'Sin cobertura' ? 'sin cobertura' : `cobertura con ${lesion.cobertura.toLowerCase()}`
    : null;
  // Última curación (dictado 2026-07-18): "si está curada y de cuándo es la
  // última curación" — la hora vive en la lesión (clave opcional, jamás texto
  // libre) y la entrega puede leerla de un vistazo.
  const curacion = HORA_LESION_RE.test(lesion.ultimaCuracionHora || '')
    ? `última curación a las ${lesion.ultimaCuracionHora}`
    : null;
  // Medidas: "de 3 x 2 cm" (o una sola dimensión). Se RE-VALIDAN al leer
  // (mismo patrón que ultimaCuracionHora): el jsonb viaja al remoto y vuelve
  // por merge, y este módulo solo imprime catálogo, así que un valor raro
  // guardado por otra vía jamás imprime.
  const medidas = medidasLesionTexto(lesion) ? `de ${medidasLesionTexto(lesion)}` : null;
  // Describir la herida (opcional): daiSeveridad va junto al tipo/zona (como
  // la categoría de LPP), el resto de fragmentos entra tras las medidas y
  // ANTES de origen/cobertura/curación, que NO se mueven de lugar.
  const [dai, lecho, exudado, bordes, perilesional, infeccion] = fragmentosDescriptores(lesion);
  return { ancla: anclaLesion(lesion), dai, medidas, lecho, exudado, bordes, perilesional, infeccion, origen, cobertura, curacion };
}

export function fraseLesion(lesion, opts = {}) {
  if (esV2(lesion)) return fraseLesionV2(lesion, opts);
  const f = fragmentosLesion(lesion, opts);
  return [
    f.ancla,
    f.dai,
    f.medidas,
    f.lecho,
    f.exudado,
    f.bordes,
    f.perilesional,
    f.infeccion,
    f.origen,
    f.cobertura,
    f.curacion,
  ].filter(Boolean).join(', ');
}

const HORA_LESION_RE = /^\d{1,2}:\d{2}$/;

// Registra la hora de la última curación de una lesión (pura, retrocompatible:
// clave opcional del mismo jsonb, sin migración). Hora inválida la limpia.
export function conUltimaCuracion(examenFisico, lesionId, hora) {
  const ex = examenFisico || {};
  const { lesiones, revisiones, prevencion } = pielState(ex);
  if (!lesiones.some((l) => l.id === lesionId)) return ex;
  const nuevas = lesiones.map((l) => (
    l.id === lesionId
      ? { ...l, ultimaCuracionHora: HORA_LESION_RE.test(hora || '') ? hora : null }
      : l
  ));
  return { ...ex, piel: { lesiones: nuevas, revisiones, prevencion } };
}

// ── La curación dictada aterriza EN su lesión (pieza 4 pre-Play) ─────────────
// "curación de sacro, limpia" ya cayó a la cronología; si el mapa tiene
// EXACTAMENTE una lesión en esa zona, la curación se le ancla: actualiza
// ultimaCuracionHora (y la cobertura, si la acción la trae). Con cero o
// varias lesiones en la zona no se toca nada (ante la ambigüedad, silencio:
// el lápiz de la lesión sigue ahí). Pura; acepta el label o el id de la zona.
export function anclarCuracion(examenFisico, { zona, lado = null, cobertura = null, hora } = {}) {
  const ex = examenFisico || {};
  const zonaTxt = String(zona || '').trim().toLowerCase();
  if (!zonaTxt) return { examenFisico: ex, lesion: null };
  const def = ZONAS_PRESION.find((z) => z.id === zonaTxt || z.label.toLowerCase() === zonaTxt);
  if (!def || def.id === 'otra') return { examenFisico: ex, lesion: null };
  const { lesiones } = pielState(ex);
  const ladoOk = lado === 'der' || lado === 'izq' ? lado : null;
  const calzaLado = (l) => !ladoOk || l.lado === null || l.lado === ladoOk;
  // Con lado dictado, calza ese lado (una lesión sin lado también cuenta:
  // es la misma zona); sin lado, cualquier lesión de la zona.
  const mismaZona = lesiones.filter((l) => l.zona === def.id).filter(calzaLado);
  // La curación que llega acá es SIEMPRE de LPP (guarda de objetivo en el
  // parser y en el efecto): una lesión de OTRO tipo jamás es candidata a
  // recibir la hora ni la cobertura (barrido 2026-08-02: una maceración sola
  // en la zona quedaba "curada"). Pero SÍ cuenta como ambigüedad: el dictado
  // "curación de sacro" pudo referirse a ella, y ante la duda, silencio.
  const enZona = mismaZona.filter((l) => l.tipo === 'LPP');
  const otrasEnZona = mismaZona.length - enZona.length;
  // Una LPP LEGADA en la misma zona también cuenta como ambigüedad (el mapa
  // la muestra junto a las nuevas; la curación dictada pudo ser de ella y a
  // una legada no se le escribe): auditoría 2026-08-01.
  const legadasEnZona = lesionesLegacySacra(ex).filter((l) => l.zona === def.id).filter(calzaLado);
  if (enZona.length !== 1 || otrasEnZona > 0 || legadasEnZona.length > 0) return { examenFisico: ex, lesion: null };
  const objetivo = enZona[0];
  if (esV2(objetivo)) {
    // La v2 guarda cada curación con su hora (y la cobertura, si el dictado la
    // trae): así la evolución la narra en su propia frase y la siguiente
    // valoración la ve en la historia.
    const st = pielState(ex);
    const actualizada = conCuracion(objetivo, { hora, cobertura: COBERTURAS_LESION.includes(cobertura) ? cobertura : undefined });
    return {
      examenFisico: { ...ex, piel: { ...ex.piel, lesiones: st.lesiones.map((l) => (l.id === objetivo.id ? actualizada : l)) } },
      lesion: objetivo,
    };
  }
  let nuevoEx = conUltimaCuracion(ex, objetivo.id, hora);
  if (COBERTURAS_LESION.includes(cobertura)) {
    const st = pielState(nuevoEx);
    nuevoEx = {
      ...nuevoEx,
      piel: {
        ...nuevoEx.piel,
        lesiones: st.lesiones.map((l) => (l.id === objetivo.id ? { ...l, cobertura } : l)),
      },
    };
  }
  return { examenFisico: nuevoEx, lesion: objetivo };
}

// ── Historia de las lesiones v2 ──────────────────────────────────────────────
// Toda valoración después de la primera, en orden de hora. La primera ya va en
// la frase de la lesión.
function valoracionesPosteriores(lesiones) {
  const out = [];
  for (const l of lesiones) {
    if (!esV2(l)) continue;
    const primera = primeraValoracion(l);
    for (const v of valoracionesDe(l)) if (v.id !== primera?.id) out.push({ l, v });
  }
  return out.sort((a, b) => (a.v.ts || 0) - (b.v.ts || 0));
}
function rotuloValoracion(v) {
  return v?.procedencia === 'curacion' ? 'En la curación' : 'En control';
}

// Líneas de piel para el texto del examen (buildExamenFisico las agrega tras
// los segmentos): la foto, y cada control con su hora. Todo sale de catálogos
// cerrados y números: no hay texto libre que pueda traer un nombre, pero sigue
// siendo un dato clínico de una persona (ver la cabecera).
export function lineasPiel(examenFisico) {
  const { lesiones, revisiones } = pielState(examenFisico);
  const lineas = [];
  // Piel SUELTA (pieza "Curación" o "Examen físico" fuera del turno, ver
  // esExamenSuelto en examenFisico.js; la marca se lee aquí sin importarla
  // porque examenFisico ya importa este módulo y no se quiere un ciclo).
  // Fuera del turno no hay "al recibir" ni "en el turno": todas las lesiones
  // van en una sola línea, sin la procedencia, con las MISMAS frases.
  if (examenFisico?.suelto === true) {
    if (lesiones.length) {
      lineas.push(`Piel y puntos de apoyo: ${lesiones.map((l) => fraseLesion(l, { origen: false })).join('; ')}.`);
    } else if (revisiones.some((r) => r.resultado === 'indemne')) {
      lineas.push(hayAlteracionesCutaneas(examenFisico) ? 'Piel y puntos de apoyo: sin lesiones por presión.' : 'Piel y puntos de apoyo: indemne, sin lesiones por presión.');
    }
    const { prevencion } = pielState(examenFisico);
    if (prevencion.length) {
      lineas.push(`Medidas de prevención de LPP: ${prevencion.map((x) => x.toLowerCase()).join(', ')}.`);
    }
    return lineas;
  }
  const indemne = revisiones.find((r) => r.contexto === 'ingreso' && r.resultado === 'indemne');
  const alIngreso = lesiones.filter((l) => l.presenteAlIngreso);
  const delTurno = lesiones.filter((l) => !l.presenteAlIngreso);
  if (alIngreso.length) {
    lineas.push(`Piel y puntos de apoyo: ${alIngreso.map((l) => fraseLesion(l)).join('; ')}.`);
  } else if (indemne) {
    lineas.push(hayAlteracionesCutaneas(examenFisico) ? 'Piel y puntos de apoyo: sin lesiones por presión al recibir.' : 'Piel y puntos de apoyo: indemne al recibir, sin lesiones por presión.');
  }
  // Los controles se dicen con contexto y sin hora (misma norma): "durante
  // el turno" ubica; cada revisión conserva su hora en los datos.
  const controles = revisiones.filter((x) => x.contexto === 'control');
  if (controles.length) {
    lineas.push(`${controles.length === 1 ? 'Control' : 'Controles'} de piel durante el turno: sin cambios.`);
  }
  if (delTurno.length) {
    lineas.push(`Hallazgo de piel en el turno: ${delTurno.map((l) => fraseLesion(l)).join('; ')}.`);
  }
  // Valoraciones posteriores de lesiones v2 (control, curación): solo lo que
  // se miró en cada una, en orden. La histórica no tiene valoraciones.
  for (const { l, v } of valoracionesPosteriores(lesiones)) {
    const frase = fraseControlV2(l, v);
    if (frase) lineas.push(`${rotuloValoracion(v)}: ${frase}.`);
  }
  const { prevencion } = pielState(examenFisico);
  if (prevencion.length) {
    lineas.push(`Medidas de prevención de LPP: ${prevencion.map((x) => x.toLowerCase()).join(', ')}.`);
  }
  return lineas;
}

// ¿Apareció piel nueva durante el turno? — el contraste ingreso vs intra-turno
// que la institución audita (y que el SBAR entrega como pendiente).
export function hayLesionNuevaIntraTurno(examenFisico) {
  return pielState(examenFisico).lesiones.some((l) => !l.presenteAlIngreso);
}

// ── Puente piel → evolución (tegumentario) ────────────────────────────────────
// Mismo contrato idempotente que mergeExamenEnEvolucion: cada lesión/revisión
// enrutada queda anotada en `evolucion._pielEnrutada`; traer dos veces no
// duplica. Agrega a la nota del tegumentario, nunca pisa lo escrito.
//
// Lesiones v2: además de la lesión, se enruta su HISTORIA (valoraciones
// posteriores, curaciones, cambios de categoría, cicatrización y correcciones)
// con una clave por suceso: `id@valoracion`, `id%curacion`, `id#n` (posición en
// el historial, que solo crece). Los sucesos que no escriben frase (porque la
// frase inicial ya los refleja) se anotan en `_pielVistos`, aparte, para que
// `_pielEnrutada` siga contando solo lo que bajó a la prosa.
const claveVal = (l, v) => `${l.id}@${v.id}`;
const claveCur = (l, c) => `${l.id}%${c.id}`;
const claveHist = (l, i) => `${l.id}#${i}`;

function yaEnrutadas(evolucion) {
  return new Set([...(evolucion?._pielEnrutada || []), ...(evolucion?._pielVistos || [])]);
}

function eventosV2(lesiones, ya) {
  const out = [];
  for (const l of lesiones) {
    if (!esV2(l)) continue;
    const nueva = !ya.has(l.id);
    const primera = primeraValoracion(l);
    for (const v of valoracionesDe(l)) {
      if (v.id === primera?.id) continue;
      const clave = claveVal(l, v);
      if (!ya.has(clave)) out.push({ tipo: 'valoracion', l, v, clave, ts: v.ts || 0 });
    }
    for (const c of (Array.isArray(l.curaciones) ? l.curaciones : [])) {
      const clave = claveCur(l, c);
      if (!ya.has(clave)) out.push({ tipo: 'curacion', l, c, clave, ts: c.ts || 0 });
    }
    (Array.isArray(l.historial) ? l.historial : []).forEach((h, i) => {
      const clave = claveHist(l, i);
      if (ya.has(clave)) return;
      const ts = h?.ts || 0;
      // Creación, valoración y curación se narran por su propia vía. Los
      // pendientes no se narran en la evolución: viajan a la entrega (SBAR).
      if (['creacion', 'valoracion', 'curacion', 'pendientes'].includes(h?.evento)) { out.push({ tipo: 'visto', clave, ts }); return; }
      // En una lesión que baja por primera vez, la frase inicial ya refleja
      // las correcciones y las definiciones; solo los cambios de hecho
      // (progresión, nueva información) se cuentan aparte.
      const deHecho = h?.evento === 'reclasificacion' && h.motivo !== 'correccion';
      if (nueva && !deHecho) { out.push({ tipo: 'visto', clave, ts }); return; }
      out.push({ tipo: h?.evento, l, h, clave, ts });
    });
  }
  return out.sort((a, b) => a.ts - b.ts);
}

export function pielEnrutable(examenFisico, evolucion) {
  const ya = yaEnrutadas(evolucion);
  const { lesiones, revisiones, prevencion } = pielState(examenFisico);
  const eventos = eventosV2(lesiones, ya);
  return {
    lesiones: lesiones.filter((l) => !ya.has(l.id)),
    revisiones: revisiones.filter((r) => !ya.has(r.id)),
    // La prevención viaja POR ÍTEM (auditoría 2026-08-01: con una sola clave
    // de set, las taloneras agregadas después del primer armado no llegaban
    // nunca): cada medida nueva se enruta; las ya enrutadas no se repiten.
    prevencion: prevencion.filter((x) => !ya.has(`prevencion:${x}`)),
    eventos: eventos.filter((e) => e.tipo !== 'visto'),
    vistos: eventos.filter((e) => e.tipo === 'visto').map((e) => e.clave),
  };
}

export function nPielEnrutable(examenFisico, evolucion) {
  const { lesiones, revisiones, prevencion, eventos } = pielEnrutable(examenFisico, evolucion);
  return lesiones.length + revisiones.length + prevencion.length + eventos.length;
}

const minus = (t) => String(t).charAt(0).toLocaleLowerCase() + String(t).slice(1);
const enLista = (xs) => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} y ${xs[xs.length - 1]}`);
const TOLERANCIA_PROSA = { 'Bien tolerada': 'bien tolerada', 'Con dolor': 'con dolor', 'Requirió suspender': 'se suspende por tolerancia' };

// La frase con que una lesión baja por primera vez: su registro inicial.
function fraseInicial(l, alRecibir) {
  const opts = alRecibir ? { origen: false } : {};
  return esV2(l) ? fraseLesion(lesionAlCrear(l), opts) : fraseLesion(l, opts);
}

// La frase de un control o curación. Va entera (con su rótulo) en `_pielFrases`
// para poder corregirla después.
function oracionValoracion(l, v) {
  const f = fraseControlV2(l, v);
  return f ? `${rotuloValoracion(v)}: ${f}.` : null;
}

function oracionEvento(ev) {
  const { l } = ev;
  if (ev.tipo === 'valoracion') return oracionValoracion(l, ev.v);
  if (ev.tipo === 'curacion') {
    const c = ev.c;
    const partes = [];
    if (Array.isArray(c.intervenciones) && c.intervenciones.length) partes.push(enLista(c.intervenciones.map(minus)));
    if (c.cobertura) partes.push(c.cobertura === 'Sin cobertura' ? 'sin cobertura' : `cobertura con ${minus(c.cobertura)}`);
    if (TOLERANCIA_PROSA[c.tolerancia]) partes.push(TOLERANCIA_PROSA[c.tolerancia]);
    const hora = /^\d{1,2}:\d{2}$/.test(c.hora || '') ? ` a las ${c.hora}` : '';
    return `Curación de ${cabezaCorta(lesionEn(l, c.ts || 0))}${hora}${partes.length ? `, ${partes.join(', ')}` : ''}.`;
  }
  const def = CLASIFICACION[l.tipo];
  const conValor = (valor) => (def ? { ...l, [def.campo]: valor } : l);
  if (ev.tipo === 'reclasificacion' && def) {
    const de = ev.h.de && ev.h.de !== PENDIENTE ? minus(CATEGORIA_LEGADO[ev.h.de] || ev.h.de) : null;
    const motivo = MOTIVO_RECLASIFICACION.find((x) => x.id === ev.h.motivo);
    const cabeza = cabezaCorta(conValor(ev.h.a));
    const antes = de ? `, antes ${concuerda(l, 'registrada')} como ${de}` : '';
    if (ev.h.motivo === 'correccion') return `Corrección del registro: ${cabeza}${antes}.`;
    return `${cabeza}${antes}${motivo ? `, cambio ${motivo.prosa}` : ''}.`;
  }
  if (ev.tipo === 'clasificacion' && def) return `Clasificación definida: ${cabezaCorta(conValor(ev.h.a))}.`;
  if (ev.tipo === 'cicatrizacion') return `${cabezaCorta(lesionEn(l, ev.ts))}, en cicatrización.`;
  if (ev.tipo === 'cicatrizacion_retirada') return `${cabezaCorta(lesionEn(l, ev.ts))}, ya no se registra en cicatrización.`;
  return null;
}

export function mergePielEnEvolucion(examenFisico, evolucion) {
  let base = evolucion && typeof evolucion === 'object' ? evolucion : { sistemas: {} };
  // Solo ajusta la frase literal que este puente escribió y cuya revisión
  // sigue registrada. Una nota reescrita a mano no se reemplaza.
  const nota = base.sistemas?.tegumentario?.nota;
  const respaldo = pielState(examenFisico).revisiones.some(r => r.contexto === 'ingreso'
    && r.resultado === 'indemne' && base._pielEnrutada?.includes(r.id));
  const corregir = respaldo && hayAlteracionesCutaneas(examenFisico)
    && typeof nota === 'string' && nota.includes(FRASE_PIEL_INDEMNE);
  if (corregir) base = { ...base, sistemas: { ...base.sistemas, tegumentario: {
    ...base.sistemas.tegumentario, nota: nota.replace(FRASE_PIEL_INDEMNE, frasePielAlRecibir(examenFisico)),
  } } };
  // `integridad` es selección múltiple desde 2026-09-29: el valor del puente es
  // ['Piel indemne'] (o el texto suelto de un turno anterior al cambio).
  const soloIndemne = (v) => v === 'Piel indemne'
    || (Array.isArray(v) && v.length === 1 && v[0] === 'Piel indemne');
  if (corregir && soloIndemne(base._puenteCampos?.['tegumentario:integridad'])
    && soloIndemne(base.sistemas.tegumentario.campos?.integridad)) {
    const campos = { ...base.sistemas.tegumentario.campos };
    delete campos.integridad;
    const libro = { ...base._puenteCampos };
    delete libro['tegumentario:integridad'];
    base = { ...base, _puenteCampos: libro, sistemas: { ...base.sistemas,
      tegumentario: { ...base.sistemas.tegumentario, campos },
    } };
  }
  const { lesiones, revisiones, prevencion, eventos, vistos } = pielEnrutable(examenFisico, base);
  if (!lesiones.length && !revisiones.length && !prevencion.length && !eventos.length && !vistos.length) {
    return { evolucion: base, agregadas: corregir ? 1 : 0 };
  }

  const sistemas = { ...(base.sistemas || {}) };
  const prev = sistemas.tegumentario || { hallazgos: [], nota: '' };
  let notaPrev = typeof prev.nota === 'string' ? prev.nota.trim() : '';
  const fraseGuardada = { ...(base._pielFrases || {}) };

  const frases = [];
  const alIngreso = lesiones.filter((l) => l.presenteAlIngreso);
  const delTurno = lesiones.filter((l) => !l.presenteAlIngreso);
  // El rótulo dice la procedencia UNA vez, con la palabra del fundador ("al
  // recibir", jamás "al ingreso", 2026-08-10); cada lesión ya no repite
  // "presente al recibir" (caso N: dos palabras para el mismo momento en la
  // misma frase). Párrafo propio, separado del examen físico.
  if (alIngreso.length) frases.push(`Al recibir: ${alIngreso.map((l) => fraseInicial(l, true)).join('; ')}.`);
  const indemne = revisiones.find((r) => r.contexto === 'ingreso' && r.resultado === 'indemne');
  if (indemne && !alIngreso.length) frases.push(frasePielAlRecibir(examenFisico));
  // Sin hora la frase de control es IDÉNTICA entre merges, y el auto-armado
  // corre en cada montaje: un segundo control enrutado después la duplicaba
  // (barrido 2026-08-02). Si la nota ya la tiene, no se re-agrega: solo se
  // promueve a plural; la revisión nueva queda marcada igual en _pielEnrutada.
  const FRASE_CONTROL = 'Control de piel durante el turno: sin cambios.';
  const FRASE_CONTROLES = 'Controles de piel durante el turno: sin cambios.';
  const controles = revisiones.filter((x) => x.contexto === 'control');
  if (controles.length) {
    if (notaPrev.includes(FRASE_CONTROL)) {
      notaPrev = notaPrev.replace(FRASE_CONTROL, FRASE_CONTROLES);
    } else if (!notaPrev.includes(FRASE_CONTROLES)) {
      frases.push(controles.length === 1 ? FRASE_CONTROL : FRASE_CONTROLES);
    }
  }
  // Las históricas detectadas en el turno van juntas, como siempre. Las v2 se
  // ordenan con su historia: una lesión detectada a las 13:40 va después de la
  // curación de las 11:00.
  const delTurnoHist = delTurno.filter((l) => !esV2(l));
  if (delTurnoHist.length) frases.push(`En el turno: ${delTurnoHist.map((l) => fraseInicial(l, false)).join('; ')}.`);
  for (const l of lesiones) {
    if (esV2(l)) fraseGuardada[l.id] = { t: fraseInicial(l, Boolean(l.presenteAlIngreso)), g: l.presenteAlIngreso ? 'r' : 't' };
  }

  // La historia v2, en orden. Una corrección reemplaza la frase que este
  // puente escribió si la nota la conserva intacta; si la autora ya la
  // reescribió, no se toca y la corrección se AGREGA con su rótulo (la traza
  // queda en el documento y en el historial de la lesión).
  const conTexto = [];
  const sinTexto = [...vistos];
  const reemplazarOAgregar = (clave, nueva, nuevaConOrigen) => {
    const guardada = fraseGuardada[clave];
    if (!guardada) return false;
    if (nueva === guardada.t) return false;
    const textoActual = [notaPrev, ...frases].join('\n');
    if (guardada.t && textoActual.includes(guardada.t) && nueva) {
      if (notaPrev.includes(guardada.t)) notaPrev = notaPrev.replace(guardada.t, nueva);
      else for (let i = 0; i < frases.length; i += 1) if (frases[i].includes(guardada.t)) { frases[i] = frases[i].replace(guardada.t, nueva); break; }
    } else if (nuevaConOrigen) {
      frases.push(`Corrección del registro: ${nuevaConOrigen.replace(/\.$/, '')}.`);
    }
    fraseGuardada[clave] = { ...guardada, t: nueva || guardada.t };
    return true;
  };
  const linea = [
    ...eventos,
    ...delTurno.filter(esV2).map((l) => ({ tipo: 'deteccion', l, ts: l.ts || 0 })),
  ].sort((a, b) => a.ts - b.ts);
  for (let i = 0; i < linea.length; i += 1) {
    const ev = linea[i];
    if (ev.tipo === 'deteccion') {
      // Detecciones seguidas comparten la oración.
      const grupo = [ev.l];
      while (linea[i + 1]?.tipo === 'deteccion') { i += 1; grupo.push(linea[i].l); }
      frases.push(`En el turno: ${grupo.map((l) => fraseInicial(l, false)).join('; ')}.`);
      continue;
    }
    if (ev.tipo === 'correccion' || ev.tipo === 'clasificacion') {
      const l = ev.l;
      const vid = ev.h?.valoracion;
      const primera = primeraValoracion(l);
      let hecho = false;
      if (vid && vid !== primera?.id) {
        const v = valoracionesDe(l).find((x) => x.id === vid);
        const nueva = v ? oracionValoracion(l, v) : null;
        hecho = reemplazarOAgregar(claveVal(l, { id: vid }), nueva, nueva && nueva.replace(/^[^:]+: /, ''));
      } else {
        const guardada = fraseGuardada[l.id];
        const grupo = l.presenteAlIngreso ? 'r' : 't';
        const nueva = fraseInicial(l, grupo === 'r');
        // Si la corrección cambió el momento (de "al recibir" a "en el turno"),
        // la frase no cabe en su oración: se agrega la corrección entera.
        if (guardada && guardada.g !== grupo) {
          frases.push(`Corrección del registro: ${fraseInicial(l, false)}.`);
          fraseGuardada[l.id] = { t: nueva, g: grupo };
          hecho = true;
        } else {
          hecho = reemplazarOAgregar(l.id, nueva, fraseInicial(l, false));
        }
      }
      if (!hecho && ev.tipo === 'clasificacion') {
        const o = oracionEvento(ev);
        if (o) { frases.push(o); conTexto.push(ev.clave); continue; }
      }
      (hecho ? conTexto : sinTexto).push(ev.clave);
      continue;
    }
    const o = oracionEvento(ev);
    if (o) {
      frases.push(o);
      conTexto.push(ev.clave);
      if (ev.tipo === 'valoracion') fraseGuardada[ev.clave] = { t: o, g: 'c' };
    } else {
      sinTexto.push(ev.clave);
    }
  }
  if (prevencion.length) frases.push(`Medidas de prevención de LPP: ${prevencion.map((x) => x.toLowerCase()).join(', ')}.`);

  const frase = frases.join(' ');
  const cambioNota = frase || notaPrev !== (typeof prev.nota === 'string' ? prev.nota.trim() : '');
  if (cambioNota || lesiones.length || revisiones.length || prevencion.length) {
    sistemas.tegumentario = {
      ...prev,
      nota: frase ? (notaPrev ? `${notaPrev}\n${frase}` : frase) : notaPrev,
      sinHallazgos: false,
    };
  }
  const claves = [
    ...(base._pielEnrutada || []),
    ...lesiones.map((l) => l.id),
    ...revisiones.map((r) => r.id),
    ...prevencion.map((x) => `prevencion:${x}`),
    ...conTexto,
  ];
  const salida = { ...base, sistemas, _pielEnrutada: claves };
  if (sinTexto.length) salida._pielVistos = [...(base._pielVistos || []), ...sinTexto];
  if (Object.keys(fraseGuardada).length) salida._pielFrases = fraseGuardada;
  return {
    evolucion: salida,
    agregadas: lesiones.length + revisiones.length + prevencion.length + conTexto.length,
  };
}

// ── Continuidad: la piel del turno anterior ───────────────────────────────────
// Para el eco "retomar de tu turno anterior" (se agrega como clave `piel` al
// retorno de resumenExamenPrevio, aditivo): una LPP registrada ayer protege al
// que recibe hoy — "venía de antes", con su detalle estructurado.
export function resumenPielPrevio(examenFisico) {
  const { lesiones } = pielState(examenFisico);
  if (!lesiones.length) return null;
  return {
    lesiones: lesiones.map((l) => {
      const out = {
        frase: fraseLesion(l),
        nivel: nivelLesion(l),
        presenteAlIngreso: Boolean(l.presenteAlIngreso),
      };
      // La v2 viaja con lo que hace falta para seguirla: su id, su tipo, su
      // clasificación y su ubicación (para proponerla igual), y la última
      // valoración como REFERENCIA. Quien recibe vuelve a mirar: la valoración
      // nueva empieza vacía.
      if (esV2(l)) {
        const def = CLASIFICACION[l.tipo];
        out.v2 = {
          id: l.id,
          serie: serieDe(l),
          tipo: l.tipo,
          ...(def && l[def.campo] ? { [def.campo]: l[def.campo] } : {}),
          ubicacion: l.ubicacion,
          ...(l.dispositivo ? { dispositivo: l.dispositivo } : {}),
          ultima: valoracionesDe(l).length ? valoracionesDe(l).reduce((a, b) => ((b.ts || 0) >= (a.ts || 0) ? b : a)) : null,
          ...(l.pendientes?.length ? { pendientes: l.pendientes } : {}),
          ...(l.cobertura ? { cobertura: l.cobertura } : {}),
        };
      }
      return out;
    }),
  };
}
